import { createSlice } from '@reduxjs/toolkit'
import StatusE from '../../types/statusE'
import { ErrorT } from '../../types/errorT'
import { NotificationT } from '../../types/notificationT'

export interface UserNotificationsSliceT {
  status: StatusE
  error: ErrorT | null
  notifications: NotificationT[]
  unreadCount: number
  isLoading: boolean
  pagination: {
    total: number
    limit: number
    offset: number
  } | null
  updatingNotifications: string[]
  isBulkReading: boolean
  isBulkDismissing: boolean
}

const initialState: UserNotificationsSliceT = {
  status: StatusE.IDLE,
  error: null,
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  pagination: null,
  updatingNotifications: [],
  isBulkReading: false,
  isBulkDismissing: false,
}

const userNotificationsSlice = createSlice({
  name: 'userNotifications',
  initialState,
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload
      if (action.payload === StatusE.LOADING) {
        state.error = null
      }
    },
    startUpdatingNotification: (state, action) => {
      const notificationId = action.payload
      if (!state.updatingNotifications.includes(notificationId)) {
        state.updatingNotifications.push(notificationId)
      }
    },
    stopUpdatingNotification: (state, action) => {
      const notificationId = action.payload
      state.updatingNotifications = state.updatingNotifications.filter(id => id !== notificationId)
    },
    setBulkReading: (state, action) => {
      state.isBulkReading = action.payload
    },
    setBulkDismissing: (state, action) => {
      state.isBulkDismissing = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.status = StatusE.ERROR
      state.isLoading = false
    },
    setNotifications: (state, action) => {
      const { notifications, pagination } = action.payload
      state.notifications = notifications
      state.pagination = pagination
      state.status = StatusE.IDLE
      state.isLoading = false

      state.unreadCount = notifications.filter((n: NotificationT) => n.status === 'sent' || n.status === 'delivered').length
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload
    },
    updateNotificationStatus: (state, action) => {
      const { notificationId, status } = action.payload
      const notification = state.notifications.find(n => n.id === notificationId)
      if (notification) {
        notification.status = status
        notification.updated = new Date().toISOString()

        state.unreadCount = state.notifications.filter((n: NotificationT) => n.status === 'sent' || n.status === 'delivered').length
      }
    },
    addNotification: (state, action) => {
      const notification = action.payload
      state.notifications.unshift(notification)
      if (notification.status === 'sent' || notification.status === 'delivered') {
        state.unreadCount += 1
      }
      if (state.pagination) {
        state.pagination.total += 1
      }
    },
    clearError: (state) => {
      state.error = null
    },
    reset: () => initialState,
  },
})

export const {
  setStatus,
  setError,
  setNotifications,
  setLoading,
  updateNotificationStatus,
  addNotification,
  clearError,
  reset,
  startUpdatingNotification,
  stopUpdatingNotification,
  setBulkReading,
  setBulkDismissing,
} = userNotificationsSlice.actions

export default userNotificationsSlice.reducer
